import * as React from 'react';
import { TextField, Switch, FormControlLabel, Button, RadioGroup, Radio, FormLabel, InputAdornment } from "@mui/material";
import { useEffect, useRef, useState } from 'react';
import { ApiRequest } from "GlobalFunctions";
import LoadingButton from '@mui/lab/LoadingButton';
import Box from "elements/Box";
import AutocompleteField from 'elements/AutocompleteField';
import ImageUploader from "elements/ImageUploader";
import { useSnackbar } from 'notistack';
import * as pdfjsLib from "pdfjs-dist";
import { BsFiletypePdf } from "react-icons/bs";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

function PdfThumb({ src }: { src: string }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setError(false);
        (async () => {
            try {
                const pdf = await (pdfjsLib as any).getDocument({ url: src, withCredentials: false }).promise;
                if (cancelled) return;
                const page = await pdf.getPage(1);
                if (cancelled) return;
                const canvas = canvasRef.current;
                if (!canvas) return;
                const vp = page.getViewport({ scale: 1 });
                const scale = (canvas.parentElement?.clientWidth || 64) / vp.width;
                const scaled = page.getViewport({ scale });
                canvas.width = scaled.width;
                canvas.height = scaled.height;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;
                await page.render({ canvasContext: ctx, viewport: scaled }).promise;
            } catch {
                if (!cancelled) setError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [src]);
    if (error) return <BsFiletypePdf size={32} color="#135095" style={{ opacity: 0.7 }} />;
    return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
}

const SPECIALIST_PROCEDURES = [
    { skill_id: 1, label: 'Curación simple',   description: 'Abrasiones, cortes, heridas superficiales' },
    { skill_id: 2, label: 'Curación avanzada', description: 'Úlceras (pie diabético, venosas, arteriales, por presión), heridas infectadas o complejas' },
    { skill_id: 3, label: 'Ostomías',          description: 'Cambio de placa' },
    { skill_id: 4, label: 'Terapia VAC',       description: 'Instalación, recambio' },
];

const GENERAL_PROCEDURES = [
    { skill_id: 5,  label: 'Curación simple',         description: 'Limpieza y cobertura de heridas básicas (abrasiones)' },
    { skill_id: 6,  label: 'Retiro de suturas',        description: 'Retiro de puntos y grapas' },
    { skill_id: 7,  label: 'Tratamiento inyectable',   description: 'Administración intramuscular o subcutánea' },
    { skill_id: 8,  label: 'Vía venosa',               description: 'Sueroterapia y medicamentos endovenosos' },
    { skill_id: 9,  label: 'Sonda urinaria',           description: 'Instalación y recambio de sonda Foley' },
    { skill_id: 10, label: 'Sonda digestiva',          description: 'Instalación y manejo de sonda nasogástrica' },
];

const buildDefaultTreatments = () => {
    const t: Record<string, any> = {};
    [...SPECIALIST_PROCEDURES, ...GENERAL_PROCEDURES].forEach(p => { t[p.skill_id] = { active: false, price: '' }; });
    return t;
};

const defaultValues: any = {
    user_id: null,
    name: '',
    lastname: '',
    email: '',
    password: '',
    role_id: 1,
    phone: '',
    is_active: false,
    deleted: false,
    image: '',
    nurse_id: null,
    university_degree: '',
    service: '',
    price: '',
    region_id: '',
    locations: [],
    skills: [],
    advanced_wound_care: false,
    online_advice: false,
    about_me: '',
    whatsapp: '',
    badge_id: '',
    plan_id: 0,
    treatments: buildDefaultTreatments(),
    home_location: '',
    sup_salud_registration: '',
    issues_boleta: false,
    plan_type: 'basico',
}

export default function AccountEditor(props: any) {

    const [formValues, setFormValues] = useState<any>(defaultValues);
    const { enqueueSnackbar } = useSnackbar();
    const [loadingButton, setLoadingButton] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [whatsappError, setWhatsappError] = useState(false);
    const [files, setFiles] = useState<any>([]);

    const [credentials, setCredentials] = useState<any[]>([]);
    const [credTitle, setCredTitle] = useState('');
    const [credFile, setCredFile] = useState<any>(null);
    const [credLoading, setCredLoading] = useState(false);
    const [preferenteTerms, setPreferenteTerms] = useState('');

    const loadDetails = async () => {
        await ApiRequest({
            url: '/users/details',
            method: 'GET',
            setResponse: (response: any) => {
                // Build treatments from active skills + stored prices
                const storedTreatments = response.treatments || {};
                const activeSkills: number[] = Array.isArray(response.skills)
                    ? response.skills.map((s: any) => typeof s === 'object' ? (s.value || s.skill_id) : Number(s))
                    : [];
                const newTreatments = buildDefaultTreatments();
                Object.keys(newTreatments).forEach(sid => {
                    const id = Number(sid);
                    const isActive = activeSkills.includes(id);
                    const price = (storedTreatments[sid] || {}).price || '';
                    newTreatments[sid] = { active: isActive, price };
                });
                setFormValues({
                    ...defaultValues,
                    ...response,
                    skills: activeSkills,
                    treatments: newTreatments,
                });
            }
        });
    }

    useEffect(() => {
        loadDetails();
        ApiRequest({
            url: '/settings/details',
            method: 'GET',
            setResponse: (r: any) => { if (r.seo?.plan_preferente_terms) setPreferenteTerms(r.seo.plan_preferente_terms); }
        });
    }, []);

    useEffect(() => {
        if (formValues?.nurse_id) {
            loadCredentials(formValues.nurse_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues?.nurse_id]);

    /*=============================================
    UPDATE ACCOUNT
    =============================================*/
    const submit = async () => {
        const isEmailValid = !formValues.email || validateEmail(formValues.email);
        const isWhatsappValid = !formValues.whatsapp || validateWhatsApp(formValues.whatsapp);

        setEmailError(!isEmailValid);
        setWhatsappError(!isWhatsappValid);

        if (!isEmailValid) {
            enqueueSnackbar('El email ingresado no es válido', { variant: 'error' });
            return;
        }
        if (!isWhatsappValid) {
            enqueueSnackbar('El WhatsApp debe tener 9 dígitos. Ingresa solo los números sin el +56.', { variant: 'error' });
            return;
        }

        const body = new FormData();
        body.append('formValues', JSON.stringify(formValues));
        if (files) body.append("image", files);

        setLoadingButton(true);
        await ApiRequest({
            url: '/users/updateAccount',
            method: 'POST',
            formData: true,
            body: body,
            setResponse: (response: any) => {
                setLoadingButton(false);
                if (response?.status === 'success') {
                    enqueueSnackbar('Perfil guardado correctamente', { variant: 'success' });
                } else {
                    enqueueSnackbar(response?.message || 'Error al guardar', { variant: 'error' });
                }
            }
        });
    }

    const validateWhatsApp = (whatsapp: string) => {
        const digits = whatsapp.replace(/^\+56/, '').replace(/\D/g, '');
        return /^\d{9}$/.test(digits);
    };
    const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

    /*=============================================
    PROCEDIMIENTOS
    =============================================*/
    const toggleProcedure = (skill_id: number, active: boolean) => {
        const treatments = { ...(formValues.treatments || buildDefaultTreatments()) };
        treatments[skill_id] = { ...(treatments[skill_id] || {}), active };
        let skills: number[] = [...(formValues.skills || [])];
        if (active) {
            if (!skills.includes(skill_id)) skills.push(skill_id);
        } else {
            skills = skills.filter((s: number) => s !== skill_id);
        }
        setFormValues({ ...formValues, treatments, skills });
    };

    const updateProcedurePrice = (skill_id: number, price: string) => {
        const treatments = { ...(formValues.treatments || buildDefaultTreatments()) };
        treatments[skill_id] = { ...(treatments[skill_id] || {}), price };
        setFormValues({ ...formValues, treatments });
    };

    /*=============================================
    CREDENCIALES
    =============================================*/
    const loadCredentials = async (nurse_id: any) => {
        if (!nurse_id) return;
        await ApiRequest({
            url: '/nurses/credentials',
            method: 'GET',
            query: { nurse_id },
            setResponse: (r: any) => {
                if (r?.status === 'success') setCredentials(r.items || []);
            }
        });
    };

    const addCredential = async () => {
        if (!formValues?.nurse_id) { enqueueSnackbar('Primero guarda tu perfil profesional', { variant: 'warning' }); return; }
        if ((credentials?.length || 0) >= 3) { enqueueSnackbar('Solo puedes subir hasta 3 credenciales', { variant: 'warning' }); return; }
        if (!credTitle || !credFile) { enqueueSnackbar('Título y archivo son requeridos', { variant: 'warning' }); return; }

        if (credFile.size > 10 * 1024 * 1024) {
            enqueueSnackbar('El archivo no puede superar 10 MB', { variant: 'warning' });
            return;
        }

        // Materializar el archivo en memoria antes de enviarlo.
        // Esto evita fallos silenciosos cuando el archivo viene de Google Drive u
        // otro almacenamiento cloud en Android (el browser no puede leerlo durante xhr.send).
        let imageBlob: Blob;
        try {
            const buffer = await credFile.arrayBuffer();
            if (buffer.byteLength === 0) throw new Error('empty');
            imageBlob = new Blob([buffer], { type: credFile.type || 'application/octet-stream' });
        } catch {
            enqueueSnackbar('No se pudo leer el archivo. Descárgalo primero al dispositivo e intenta de nuevo.', { variant: 'error' });
            return;
        }

        const body = new FormData();
        body.append('formValues', JSON.stringify({ nurse_id: formValues.nurse_id, title: credTitle }));
        body.append('image', imageBlob, credFile.name);
        setCredLoading(true);
        await ApiRequest({
            url: '/nurses/credentials',
            method: 'POST',
            formData: true,
            body,
            setResponse: (r: any) => {
                setCredLoading(false);
                if (r?.status === 'success') {
                    enqueueSnackbar(r.message || 'Certificado guardado correctamente', { variant: 'success' });
                    setCredTitle(''); setCredFile(null);
                    loadCredentials(formValues.nurse_id);
                } else {
                    enqueueSnackbar(r?.message || 'No se pudo guardar', { variant: 'error' });
                }
            }
        });
    };

    const toggleVisibility = async (credential_id: number, is_visible: boolean) => {
        setCredentials(prev => prev.map(c => c.credential_id === credential_id ? { ...c, is_visible: is_visible ? 1 : 0 } : c));
        await ApiRequest({
            url: '/nurses/credentials/toggle',
            method: 'POST',
            body: { credential_id, is_visible },
            setResponse: () => {}
        });
    };

    const deleteCredential = async (credential_id: any) => {
        await ApiRequest({
            url: '/nurses/credentials/delete',
            method: 'POST',
            body: { credential_id },
            setResponse: (r: any) => {
                if (r?.status === 'success') {
                    enqueueSnackbar(r.message || 'Credencial eliminada', { variant: 'success' });
                    loadCredentials(formValues.nurse_id);
                } else {
                    enqueueSnackbar(r?.message || 'No se pudo eliminar', { variant: 'error' });
                }
            }
        });
    };

    return (
        <div className="content">
            <Box className='intent-auth_component'>

                {/* ── IMAGEN + DATOS BÁSICOS ── */}
                <div className='section_flex'>
                    <ImageUploader
                        className="user_image"
                        image={formValues.image}
                        setImage={(newImage: any) => setFormValues({ ...formValues, image: newImage })}
                        setFiles={setFiles}
                    />

                    <div className='account_right'>
                        <div className='input_group'>
                            <div className='input_row'>
                                <TextField
                                    label="Nombre"
                                    value={formValues.name}
                                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                                <TextField
                                    label="Apellido"
                                    value={formValues.lastname}
                                    onChange={(e) => setFormValues({ ...formValues, lastname: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                            </div>

                            <div className='input_row'>
                                <TextField
                                    label="Título Universitario"
                                    value={formValues.university_degree}
                                    onChange={(e) => setFormValues({ ...formValues, university_degree: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                                <TextField
                                    label="Servicio"
                                    value={formValues.service}
                                    onChange={(e) => setFormValues({ ...formValues, service: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                                <TextField
                                    label="Precio"
                                    value={formValues.price}
                                    onChange={(e) => setFormValues({ ...formValues, price: e.target.value })}
                                    variant="outlined" fullWidth
                                />
                            </div>

                            <AutocompleteField
                                label="Region"
                                endpoint="/locations/regiones/list"
                                value={formValues.region_id}
                                onChange={(data: any) => setFormValues({ ...formValues, region_id: data.region_id })}
                            />

                            {formValues.plan_id !== 0 &&
                                <AutocompleteField
                                    label="Distintivo"
                                    endpoint="/badges/list"
                                    value={formValues.badge_id}
                                    onChange={(data: any) => setFormValues({ ...formValues, badge_id: data.badge_id })}
                                />
                            }
                        </div>
                    </div>
                </div>

                <div className="input_group">

                    {/* ── CONTACTO ── */}
                    <div className='input_row'>
                        <TextField
                            label="Número de Teléfono"
                            value={formValues.phone.replace(/^\+56/, '').replace(/\D/g, '')}
                            onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '');
                                const updates: any = { phone: digits };
                                if (!formValues.whatsapp) updates.whatsapp = '+56' + digits;
                                setFormValues({ ...formValues, ...updates });
                            }}
                            inputProps={{ maxLength: 9 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">+56</InputAdornment> }}
                            variant="outlined" fullWidth
                            helperText={!formValues.whatsapp ? 'Se usará también como WhatsApp' : ''}
                        />
                        <TextField
                            label="Correo Electrónico"
                            value={formValues.email}
                            onChange={(e) => {
                                setFormValues({ ...formValues, email: e.target.value });
                                setEmailError(!validateEmail(e.target.value) && e.target.value !== '');
                            }}
                            error={emailError}
                            helperText={emailError ? 'Correo inválido' : ''}
                            variant="outlined" fullWidth
                        />
                        {formValues.plan_id !== 0 &&
                            <TextField
                                label="WhatsApp"
                                value={formValues.whatsapp.replace(/^\+56/, '').replace(/\D/g, '')}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    const full = digits ? '+56' + digits : '';
                                    setFormValues({ ...formValues, whatsapp: full });
                                    setWhatsappError(digits.length > 0 && digits.length < 9);
                                }}
                                error={whatsappError}
                                helperText={whatsappError ? 'WhatsApp inválido (9 dígitos)' : 'Ingresa solo los dígitos sin el +56. Ej: 912345678'}
                                inputProps={{ maxLength: 9 }}
                                InputProps={{ startAdornment: <InputAdornment position="start">+56</InputAdornment> }}
                                variant="outlined" fullWidth
                            />
                        }
                    </div>

                    {/* ── UBICACIÓN DE ATENCIÓN ── */}
                    {formValues.region_id && formValues.region_id !== '' && (
                        <AutocompleteField
                            label="Comunas de atención"
                            endpoint="/locations/comunas/list"
                            endpoint_params={{ region_id: formValues.region_id }}
                            multiple={true}
                            value={formValues.locations}
                            onChange={(data: any) => setFormValues({ ...formValues, locations: data ? data : [] })}
                        />
                    )}

                    {/* ── REGISTRO SUPERINTENDENCIA + COMUNA RESIDENCIA ── */}
                    <div className='input_row'>
                        <TextField
                            label="Registro de la Superintendencia de Salud"
                            value={formValues.sup_salud_registration}
                            onChange={(e) => setFormValues({ ...formValues, sup_salud_registration: e.target.value })}
                            variant="outlined" fullWidth
                        />
                        <TextField
                            label="Comuna de residencia"
                            value={formValues.home_location}
                            onChange={(e) => setFormValues({ ...formValues, home_location: e.target.value })}
                            variant="outlined" fullWidth
                            helperText="Privado — no visible en tu perfil público"
                        />
                    </div>

                    {/* ── TIPO DE SERVICIO ── */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.online_advice}
                                onChange={(e) => setFormValues({ ...formValues, online_advice: e.target.checked })}
                            />
                        }
                        label="Asesoría en línea"
                    />

                    {/* ── BOLETA ── */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!formValues.issues_boleta}
                                onChange={(e) => setFormValues({ ...formValues, issues_boleta: e.target.checked })}
                            />
                        }
                        label="Emito boleta de honorarios"
                        sx={{ mt: 0.5 }}
                    />

                    {/* ── PROCEDIMIENTOS Y VALORES REFERENCIALES ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 4px 0' }}>Procedimientos y Valores Referenciales</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', opacity: 0.75 }}>
                            Activa los procedimientos que realizas e indica un valor referencial para cada uno.
                        </p>
                        {(formValues.advanced_wound_care
                            ? [...SPECIALIST_PROCEDURES, ...GENERAL_PROCEDURES.filter(p => p.skill_id !== 5)]
                            : GENERAL_PROCEDURES
                        ).map((proc, i, arr) => {
                            const current = (formValues.treatments || {})[proc.skill_id] || { active: false, price: '' };
                            const isLast = i === arr.length - 1;
                            return (
                                <div
                                    key={proc.skill_id}
                                    style={{
                                        paddingBottom: isLast ? 0 : '12px',
                                        marginBottom: isLast ? 0 : '12px',
                                        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                                        <FormControlLabel
                                            style={{ minWidth: '240px', margin: 0, alignItems: 'flex-start' }}
                                            control={
                                                <Switch
                                                    checked={!!current.active}
                                                    onChange={(e) => toggleProcedure(proc.skill_id, e.target.checked)}
                                                />
                                            }
                                            label={
                                                <div style={{ paddingTop: '8px' }}>
                                                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{proc.label}</div>
                                                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{proc.description}</div>
                                                </div>
                                            }
                                        />
                                        {current.active && (
                                            <TextField
                                                id={`price-${proc.skill_id}`}
                                                label="Valor referencial ($)"
                                                value={current.price}
                                                onChange={(e) => updateProcedurePrice(proc.skill_id, e.target.value.replace(/[^0-9]/g, ''))}
                                                variant="outlined"
                                                size="small"
                                                style={{ width: '200px', marginTop: '6px' }}
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Box>

                    {/* ── PLAN DE SERVICIO ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <FormLabel style={{ fontWeight: 600, fontSize: '15px', display: 'block', marginBottom: '12px' }}>
                            Plan de servicio
                        </FormLabel>
                        <RadioGroup
                            value={formValues.plan_type || 'basico'}
                            onChange={(e) => setFormValues({ ...formValues, plan_type: e.target.value })}
                        >
                            <FormControlLabel
                                value="basico"
                                control={<Radio />}
                                label={
                                    <div>
                                        <strong>Plan Básico</strong>
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                            Los pacientes pueden contactarte solo por mensajes internos de la plataforma
                                        </div>
                                    </div>
                                }
                            />
                            <FormControlLabel
                                value="preferente"
                                control={<Radio />}
                                style={{ marginTop: '8px' }}
                                label={
                                    <div>
                                        <strong>Plan Preferente</strong>
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                            Los pacientes pueden contactarte por mensajes internos <strong>y por WhatsApp directo</strong>
                                        </div>
                                    </div>
                                }
                            />
                        </RadioGroup>

                        {formValues.plan_type === 'preferente' && preferenteTerms && (
                            <div style={{
                                marginTop: '12px',
                                padding: '14px 16px',
                                background: '#fffbeb',
                                border: '1px solid #fbbf24',
                                borderRadius: '8px',
                                fontSize: '13px',
                                color: '#78350f',
                                lineHeight: 1.6,
                            }}>
                                <div style={{ fontWeight: 600, marginBottom: '6px', color: '#92400e' }}>
                                    Condiciones del Plan Preferente
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: preferenteTerms }} />
                            </div>
                        )}
                    </Box>

                    {/* ── SOBRE MÍ + ESTADO ── */}
                    <div>
                        <TextField
                            label="Sobre mí"
                            value={formValues.about_me}
                            onChange={(e) => setFormValues({ ...formValues, about_me: e.target.value })}
                            variant="outlined" fullWidth multiline rows={4}
                            placeholder="Cuéntale a los pacientes quién eres: tu experiencia, especialización, años en el área, tipo de atenciones que realizas y cualquier detalle que te diferencie. Un perfil completo genera más confianza y aumenta las posibilidades de que te elijan."
                        />
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 2px' }}>
                            💡 Los pacientes leen esto antes de contactarte. Cuanto más completo, mayor confianza generas.
                        </p>
                    </div>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.is_active}
                                onChange={(e) => setFormValues({ ...formValues, is_active: e.target.checked })}
                            />
                        }
                        label="Perfil activo"
                    />

                    {/* ── CREDENCIALES ── */}
                    <Box className="intent-box" style={{ marginTop: '16px' }}>
                        <h3 style={{ margin: '0 0 4px 0' }}>Certificados y Especializaciones</h3>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#475569' }}>
                            Sube tus <b>diplomados, cursos o magíster</b> relacionados con curación de heridas.
                        </p>
                        <div style={{ margin: '0 0 12px 0', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                            ✅ Las enfermeras con certificados verificados tienen <b>mayor visibilidad</b> y son elegidas con más frecuencia por los pacientes. Puedes subir hasta 3 documentos (imagen o PDF).
                        </div>

                        {!formValues?.nurse_id ? (
                            <p style={{ fontSize: '13px', opacity: 0.6 }}>
                                Primero guarda tu perfil profesional para poder subir credenciales.
                            </p>
                        ) : (
                            <>
                                <div className="input_row">
                                    <TextField
                                        label="Título de la credencial"
                                        value={credTitle}
                                        onChange={(e) => setCredTitle(e.target.value)}
                                        variant="outlined" fullWidth
                                    />
                                    <Button variant="outlined" component="label" sx={{ minWidth: '210px', height: '56px' }}>
                                        {credFile?.name ? credFile.name.slice(0, 22) + (credFile.name.length > 22 ? '…' : '') : 'Seleccionar archivo'}
                                        <input
                                            type="file" hidden accept="image/*,.pdf,application/pdf"
                                            onChange={(e: any) => {
                                                const f = e?.target?.files?.[0];
                                                if (f) setCredFile(f);
                                            }}
                                        />
                                    </Button>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                                    <LoadingButton
                                        size="small" variant="contained"
                                        onClick={addCredential}
                                        loading={credLoading}
                                        disabled={(credentials?.length || 0) >= 3 || credLoading}
                                    >
                                        Agregar certificado
                                    </LoadingButton>
                                    <span style={{ fontSize: '12px', opacity: 0.6 }}>
                                        {credentials?.length || 0}/3
                                    </span>
                                </div>

                                {credentials && credentials.length > 0 && (
                                    <div style={{ marginTop: '12px', display: 'grid', gap: '10px' }}>
                                        {credentials.map((c: any) => (
                                            <div key={c.credential_id} style={{ display: 'flex', gap: '12px', alignItems: 'center', border: '1px solid #e6e6e6', borderRadius: '10px', padding: '10px' }}>
                                                <div style={{ width: '64px', height: '64px', borderRadius: '8px', overflow: 'hidden', background: '#f5f5f5', flex: '0 0 64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {c.image && (c.image.toLowerCase().endsWith('.pdf')
                                                        ? <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                                                            <PdfThumb src={c.image} />
                                                          </a>
                                                        : <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                                                            <img src={c.image} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                          </a>
                                                    )}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600 }}>{c.title}</div>
                                                    <div style={{ fontSize: '12px', opacity: 0.65 }}>{c.created_at || ''}</div>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                size="small"
                                                                checked={!!c.is_visible}
                                                                onChange={(e) => toggleVisibility(c.credential_id, e.target.checked)}
                                                            />
                                                        }
                                                        label={<span style={{ fontSize: '11px', color: c.is_visible ? '#15803d' : '#94a3b8' }}>{c.is_visible ? 'Activo en tu perfil' : 'Oculto en tu perfil'}</span>}
                                                        labelPlacement="bottom"
                                                        style={{ margin: 0 }}
                                                    />
                                                    <Button variant="text" color="error" size="small" onClick={() => deleteCredential(c.credential_id)}>
                                                        Eliminar
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </Box>

                    <LoadingButton
                        size="small" type="submit" fullWidth variant="contained"
                        sx={{ fontSize: '13px', padding: '10px 0px' }}
                        onClick={submit} loading={loadingButton}
                    >
                        Guardar
                    </LoadingButton>

                </div>

            </Box>
        </div>
    );
}
